import { Container, createStyles, makeStyles, Theme } from '@material-ui/core';
import { KBLinks } from 'features/constants';
import { AsyncStatusType } from 'features/files/fincen-reporting/constants';
import { PropertyAddress, PropertyData } from 'features/files/fincen-reporting/types';
import React, { FC } from 'react';
import AccordionContent from 'ui-kit/components/accordion/AccordionContent';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import PropertyAddressForm from './components/PropertyAddressForm';

export interface PropertyInformationContentProps {
  properties: PropertyData[];
  status: AsyncStatusType;
  errors?: any;
  onRefresh: () => void;
  onAddProperty: () => void;
  onPropertyChange?: (index: number, updatedProperty: PropertyData) => void;
  onDeleteProperty?: (index: number, id: string) => void;
  fileData?: any;
  menuCollapsed?: boolean;
  formSubmitted?: boolean;
  isReadOnly?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    propertyCardContainer: {
      marginBottom: theme.spacing(3),
    },
    addPropertyButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
  }),
);

const PropertyInformationContent: FC<PropertyInformationContentProps> = (props) => {
  const {
    properties,
    status,
    onAddProperty,
    onPropertyChange,
    onDeleteProperty,
    formSubmitted = false,
    isReadOnly = false,
  } = props;
  const classes = useStyles();

  const handlePropertyChange = (index: number, updatedProperty: PropertyData) => {
    if (onPropertyChange) {
      onPropertyChange(index, updatedProperty);
    }
  };

  return (
    <div
      style={{ pointerEvents: isReadOnly ? 'none' : 'auto', opacity: isReadOnly ? 0.6 : 1 }}
      tabIndex={isReadOnly ? -1 : undefined}
      onKeyDown={(e) => isReadOnly && e.preventDefault()}
    >
      <>
        <LoadingSpinner status={status} variant="linear" />
        <PageHeader
          title="Property Information"
          subtitle="The report must include information about the property involved in the reportable transfer. Multiple properties may be recorded."
          menuItems={[
            {
              label: 'Knowledge Base',
              link: KBLinks.fincenReport,
            },
          ]}
          data-testid="fincen-property-page-header"
          contentRight={
            <div className={classes.addPropertyButton}>
              <AgentNetButton
                color="primary"
                size="small"
                variant="contained"
                onClick={onAddProperty}
                data-testid="fincen-property-add-button"
                plusIcon
                disabled={properties.length >= 5}
              >
                Add Property
              </AgentNetButton>
            </div>
          }
        />

        <Container>
          {properties.map((property, index) => (
            <div key={property.id} className={classes.propertyCardContainer}>
              <AccordionContent
                id={property.id}
                title={`Property No. ${index + 1}`}
                expanded={true}
                hideSubtitle={true}
                subtitle={[]}
                onDelete={
                  onDeleteProperty && properties.length > 1 && index > 0
                    ? () => onDeleteProperty(index, property.id)
                    : undefined
                }
                deleteQAAttribute={`fincen-property-${index}-delete-button`}
                data-testid={`fincen-property-${index}-accordion`}
              >
                <PropertyAddressForm
                  address={property.address}
                  onChange={(updatedAddress: PropertyAddress) =>
                    handlePropertyChange(index, { ...property, address: updatedAddress })
                  }
                  submitted={formSubmitted}
                  index={index}
                  fileData={props.fileData}
                />
              </AccordionContent>
            </div>
          ))}
        </Container>
      </>
    </div>
  );
};

export default React.memo(PropertyInformationContent);
